import { updateSmartTextPlaceholder } from "lib/textUtils";
import { formatDocumentDateValue } from "./formatDate";
import { formatDocumentNumberValue } from "./formatDecimal";
import {
  DocumentSmartTextDateFormat,
  DocumentSmartTextType
} from "./types";

interface DocumentSmartTextFieldLike {
  value?: string | null;
  defaultValue?: string;
  type?: DocumentSmartTextType;
  format?: DocumentSmartTextDateFormat;
  requireDecimals?: boolean;
}

interface DesignLike {
  documentSmartText?: Record<string, DocumentSmartTextFieldLike>;
}

interface Options {
  wrapDocumentTokens?: boolean;
}

/**
 * Build the Smart Text resolution context used by canvas/display refreshes.
 * It merges design-scoped Document Smart Data into the normal Smart Text
 * namespace as `doc.<id>` entries, resolving any user/team Smart Text tokens in
 * document values while deliberately not resolving document-to-document tokens.
 */
export const buildDocumentSmartTextState = (
  design: DesignLike | null | undefined,
  smartTextState: Record<string, unknown> | null | undefined,
  options: Options = {}
): Record<string, unknown> | null | undefined => {
  const wrapDocumentTokens = options.wrapDocumentTokens !== false;
  const documentEntries = design?.documentSmartText || {};

  if (!wrapDocumentTokens || Object.keys(documentEntries).length === 0) {
    return smartTextState;
  }

  const nonDocumentContext = smartTextState || {};
  const documentResolvedContext: Record<string, { value: string }> = {};

  Object.keys(documentEntries).forEach(id => {
    const entry = documentEntries[id];
    const rawValue =
      entry && (entry.value || entry.value === "")
        ? entry.value
        : entry?.defaultValue || "";

    const fieldType = entry?.type ?? "text";
    const dateFormatted = formatDocumentDateValue(
      rawValue,
      fieldType,
      entry?.format
    );
    const sourceValue = formatDocumentNumberValue(
      dateFormatted,
      fieldType,
      entry?.requireDecimals
    );

    documentResolvedContext[`doc.${id}`] = {
      value: updateSmartTextPlaceholder(sourceValue, nonDocumentContext)
    };
  });

  return {
    ...smartTextState,
    ...documentResolvedContext
  };
};
